import { EXPORT_MAX_ROWS, SearchParams } from '@elsa/contracts';
import {
  Button,
  Card,
  ErrorBox,
  Field,
  Input,
  Link,
  Loading,
  Note,
  navigate,
  Page,
  replaceSearch,
  Select,
  Table,
  Td,
  useSearch,
} from '@elsa/ui';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useMemo, useState } from 'react';
import { type Column, download, toCsv } from '../lib/csv.ts';
import { fmtDay, fmtLei, fmtNum, plural } from '../lib/format.ts';
import { useOrg } from '../lib/session.tsx';
import { rpc } from '../lib/supabase.ts';
import type { AcqRow, ItemRow, Page as ResultPage } from '../lib/types.ts';
import { historyLabel, useFilterOptions } from './common.tsx';
import { SavedSearchList, SaveSearchButton } from './Saved.tsx';

// Cautarea (docs/22 §2): filtrele stau in URL (?q=…&cpv=…), deci o cautare se poate trimite unui
// coleg. Rezultatele vin pe pagini de 50, cu cursorul (ziua, id) din app.search_*.

type Kind = 'acq' | 'item';

const FIELDS = [
  'q',
  'cpv',
  'category',
  'county',
  'day_from',
  'day_to',
  'value_min',
  'value_max',
] as const;

/** URL -> parametrii RPC (validati cu aceeasi schema ca in SQL). */
export function paramsFromUrl(sp: URLSearchParams): {
  params: Record<string, unknown>;
  error: string | null;
} {
  const raw: Record<string, unknown> = {};
  for (const k of FIELDS) {
    const v = sp.get(k)?.trim();
    if (!v) continue;
    raw[k] = ['category', 'value_min', 'value_max'].includes(k) ? Number(v.replace(',', '.')) : v;
  }
  for (const k of ['authority_id', 'supplier_id'] as const) {
    const v = sp.get(k);
    if (v) raw[k] = Number(v);
  }
  const r = SearchParams.safeParse(raw);
  if (!r.success) return { params: {}, error: r.error.issues[0]?.message ?? 'Filtre invalide' };
  return { params: r.data as Record<string, unknown>, error: null };
}

const ACQ_CSV: Column<AcqRow>[] = [
  { label: 'Data', value: (r) => r.finalization_day },
  { label: 'Cod', value: (r) => r.code },
  { label: 'Denumire', value: (r) => r.name },
  { label: 'CPV', value: (r) => r.cpv_code },
  { label: 'Descriere CPV', value: (r) => r.cpv_label },
  { label: 'Valoare finală (lei)', value: (r) => r.closing_value_ron },
  { label: 'Valoare estimată (lei)', value: (r) => r.estimated_value_ron },
  { label: 'Autoritate', value: (r) => r.authority_name },
  { label: 'Județ', value: (r) => r.authority_county },
  { label: 'Furnizor', value: (r) => r.supplier_name },
  { label: 'Id SEAP', value: (r) => r.id },
  { label: 'Link Elsa', value: (r) => `${location.origin}/app/achizitii/${r.id}` },
];

const ITEM_CSV: Column<ItemRow>[] = [
  { label: 'Data', value: (r) => r.finalization_day },
  { label: 'Produs', value: (r) => r.name },
  { label: 'CPV', value: (r) => r.cpv_code },
  { label: 'UM', value: (r) => r.measure_unit },
  { label: 'Cantitate', value: (r) => r.quantity },
  { label: 'Preț unitar (lei)', value: (r) => r.unit_price_ron },
  { label: 'Total (lei)', value: (r) => r.line_total_ron },
  { label: 'Autoritate', value: (r) => r.authority_name },
  { label: 'Județ', value: (r) => r.authority_county },
  { label: 'Furnizor', value: (r) => r.supplier_name },
  { label: 'Achiziția', value: (r) => r.acquisition_id },
];

function Filters({ kind }: { kind: Kind }) {
  const sp = useSearch();
  const opts = useFilterOptions();
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const next = new URLSearchParams();
    for (const k of FIELDS) {
      const v = String(f.get(k) ?? '').trim();
      if (v) next.set(k, v);
    }
    for (const k of ['authority_id', 'supplier_id']) {
      const v = sp.get(k);
      if (v) next.set(k, v);
    }
    replaceSearch(next);
  };
  return (
    <form onSubmit={submit} className="space-y-3" key={sp.toString()}>
      <div className="flex gap-2">
        <Input
          name="q"
          defaultValue={sp.get('q') ?? ''}
          placeholder={
            kind === 'acq'
              ? 'Ce cauți? ex. seringi, hârtie A4, „servicii de curățenie”'
              : 'Produsul, ex. mănuși nitril'
          }
          aria-label="Cuvinte cheie"
          maxLength={200}
        />
        <Button tone="primary" type="submit">
          Caută
        </Button>
      </div>
      <details className="text-sm" open={FIELDS.slice(1).some((k) => sp.get(k))}>
        <summary className="eticheta cursor-pointer text-cerneala select-none">Filtre</summary>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Cod CPV (prefix)" help="ex. 3314 sau 33141110">
            <Input name="cpv" defaultValue={sp.get('cpv') ?? ''} inputMode="numeric" />
          </Field>
          <Field label="Categoria">
            <Select name="category" defaultValue={sp.get('category') ?? ''}>
              <option value="">Toate</option>
              {opts.data?.categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Județul autorității">
            <Select name="county" defaultValue={sp.get('county') ?? ''}>
              <option value="">Toate</option>
              {opts.data?.counties.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={kind === 'acq' ? 'Valoare (lei)' : 'Preț unitar (lei)'}>
            <div className="flex gap-2">
              <Input
                name="value_min"
                defaultValue={sp.get('value_min') ?? ''}
                placeholder="min"
                inputMode="decimal"
              />
              <Input
                name="value_max"
                defaultValue={sp.get('value_max') ?? ''}
                placeholder="max"
                inputMode="decimal"
              />
            </div>
          </Field>
          <Field label="De la data">
            <Input name="day_from" type="date" defaultValue={sp.get('day_from') ?? ''} />
          </Field>
          <Field label="Până la data">
            <Input name="day_to" type="date" defaultValue={sp.get('day_to') ?? ''} />
          </Field>
        </div>
      </details>
    </form>
  );
}

function AcqTable({ rows }: { rows: AcqRow[] }) {
  return (
    <Table
      head={['Data', 'Achiziția', 'Autoritatea', 'Furnizorul', 'Valoare']}
      empty={!rows.length}
    >
      {rows.map((r) => (
        <tr key={r.id}>
          <Td className="whitespace-nowrap tabular-nums">{fmtDay(r.finalization_day)}</Td>
          <Td className="max-w-md">
            <Link to={`/app/achizitii/${r.id}`} className="font-medium hover:underline">
              {r.name ?? `Achiziția ${r.id}`}
            </Link>
            <div className="text-xs text-gri-700">
              {r.cpv_code} {r.cpv_label}
            </div>
          </Td>
          <Td className="max-w-xs">
            {r.authority_id ? (
              <Link to={`/app/autoritati/${r.authority_id}`} className="hover:underline">
                {r.authority_name}
              </Link>
            ) : (
              r.authority_name
            )}
            <div className="text-xs text-gri-700">{r.authority_county}</div>
          </Td>
          <Td className="max-w-xs">
            {r.supplier_id ? (
              <Link to={`/app/furnizori/${r.supplier_id}`} className="hover:underline">
                {r.supplier_name}
              </Link>
            ) : (
              r.supplier_name
            )}
          </Td>
          <Td className="whitespace-nowrap text-right tabular-nums">
            {fmtLei(r.closing_value_ron ?? r.estimated_value_ron)}
          </Td>
        </tr>
      ))}
    </Table>
  );
}

function ItemTable({ rows }: { rows: ItemRow[] }) {
  return (
    <Table
      head={['Data', 'Produsul', 'Cantitate', 'Preț unitar', 'Autoritatea', 'Furnizorul']}
      empty={!rows.length}
    >
      {rows.map((r) => (
        <tr key={r.id}>
          <Td className="whitespace-nowrap tabular-nums">{fmtDay(r.finalization_day)}</Td>
          <Td className="max-w-md">
            <Link to={`/app/achizitii/${r.acquisition_id}`} className="font-medium hover:underline">
              {r.name ?? `Produsul ${r.id}`}
            </Link>
            <div className="text-xs text-gri-700">
              {r.cpv_code ? (
                <Link to={`/app/preturi/${r.cpv_code}`} className="hover:underline">
                  {r.cpv_code}
                </Link>
              ) : null}{' '}
              {r.cpv_label}
            </div>
          </Td>
          <Td className="whitespace-nowrap tabular-nums">
            {fmtNum(r.quantity)} {r.measure_unit}
          </Td>
          <Td className="whitespace-nowrap text-right tabular-nums">{fmtLei(r.unit_price_ron)}</Td>
          <Td className="max-w-xs">
            {r.authority_id ? (
              <Link to={`/app/autoritati/${r.authority_id}`} className="hover:underline">
                {r.authority_name}
              </Link>
            ) : (
              r.authority_name
            )}
          </Td>
          <Td className="max-w-xs">
            {r.supplier_id ? (
              <Link to={`/app/furnizori/${r.supplier_id}`} className="hover:underline">
                {r.supplier_name}
              </Link>
            ) : (
              r.supplier_name
            )}
          </Td>
        </tr>
      ))}
    </Table>
  );
}

/** Regulile unei alerte din filtrele cautarii (pentru „Salvează ca alertă”). */
export function rulesFromParams(p: Record<string, unknown>): Record<string, unknown> {
  const r: Record<string, unknown> = {};
  if (typeof p.q === 'string' && p.q) r.keywords = [p.q];
  if (typeof p.cpv === 'string') r.cpv_prefixes = [p.cpv];
  if (typeof p.category === 'number') r.categories = [p.category];
  if (typeof p.county === 'string') r.counties = [p.county];
  if (typeof p.authority_id === 'number') r.authority_id = p.authority_id;
  if (typeof p.value_min === 'number') r.value_min = p.value_min;
  if (typeof p.value_max === 'number') r.value_max = p.value_max;
  return r;
}

export function Results({ kind, params }: { kind: Kind; params: Record<string, unknown> }) {
  const { org, can } = useOrg();
  const qc = useQueryClient();
  const [exported, setExported] = useState<string | null>(null);
  const q = useInfiniteQuery({
    queryKey: ['search', kind, params],
    initialPageParam: null as ResultPage<unknown>['next'],
    queryFn: ({ pageParam }) =>
      rpc<ResultPage<AcqRow | ItemRow>>(kind === 'acq' ? 'search_acquisitions' : 'search_items', {
        p: { ...params, ...(pageParam ? { cursor: pageParam } : {}), limit: 50 },
      }),
    getNextPageParam: (last) => last.next,
  });
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.rows) ?? [], [q.data]);
  const exp = useMutation({
    mutationFn: () =>
      rpc<{ rows: (AcqRow | ItemRow)[]; truncated: boolean; quota: { remaining: number | null } }>(
        'export_rows',
        {
          p_org: org.id,
          p_kind: kind,
          p: params,
        },
      ),
    onSuccess: (r) => {
      const csv =
        kind === 'acq' ? toCsv(r.rows as AcqRow[], ACQ_CSV) : toCsv(r.rows as ItemRow[], ITEM_CSV);
      download(
        `elsa-${kind === 'acq' ? 'achizitii' : 'produse'}-${new Date().toISOString().slice(0, 10)}.csv`,
        csv,
      );
      setExported(
        `${r.rows.length} rânduri exportate${r.truncated ? ` (maxim ${fmtNum(EXPORT_MAX_ROWS)} pe export sau plafonul lunar)` : ''}.` +
          (r.quota.remaining === null
            ? ''
            : ` Mai poți exporta ${fmtNum(r.quota.remaining)} rânduri luna aceasta.`),
      );
      void qc.invalidateQueries({ queryKey: ['export_quota'] });
    },
  });
  const alertRules = rulesFromParams(params);
  const canAlert =
    can('alerts.own') &&
    (alertRules.keywords ||
      alertRules.cpv_prefixes ||
      alertRules.categories ||
      alertRules.authority_id);

  return (
    <Card
      title={
        q.isSuccess
          ? `${q.hasNextPage ? 'Peste ' : ''}${plural(rows.length, 'rezultat', 'rezultate')}`
          : 'Rezultate'
      }
      actions={
        <div className="flex flex-wrap gap-2">
          {Object.keys(params).some((k) => k !== 'authority_id' && k !== 'supplier_id') ? (
            <SaveSearchButton kind={kind} params={params} />
          ) : null}
          {canAlert ? (
            <Button
              onClick={() =>
                navigate(`/app/alerte?reguli=${encodeURIComponent(JSON.stringify(alertRules))}`)
              }
            >
              Salvează ca alertă
            </Button>
          ) : null}
          {can('exports.create') ? (
            <Button disabled={exp.isPending || !rows.length} onClick={() => exp.mutate()}>
              {exp.isPending ? 'Se exportă…' : 'Export CSV'}
            </Button>
          ) : null}
        </div>
      }
    >
      <div className="space-y-3">
        <ErrorBox error={q.error ?? exp.error} />
        {exported ? <Note>{exported}</Note> : null}
        {q.isLoading ? (
          <Loading />
        ) : kind === 'acq' ? (
          <AcqTable rows={rows as AcqRow[]} />
        ) : (
          <ItemTable rows={rows as ItemRow[]} />
        )}
        {q.hasNextPage ? (
          <Button
            className="w-full"
            disabled={q.isFetchingNextPage}
            onClick={() => void q.fetchNextPage()}
          >
            {q.isFetchingNextPage ? 'Se încarcă…' : 'Încarcă mai multe'}
          </Button>
        ) : null}
      </div>
    </Card>
  );
}

export function SearchPage({ kind }: { kind: Kind }) {
  const sp = useSearch();
  const { entitlements } = useOrg();
  const { params, error } = paramsFromUrl(sp);
  return (
    <Page title={kind === 'acq' ? 'Achiziții directe' : 'Produse'}>
      <Card>
        <Filters kind={kind} />
      </Card>
      <SavedSearchList kind={kind} />
      <p className="text-xs text-gri-700">
        Planul tău vede {historyLabel(entitlements?.features.history_days)}.{' '}
        {kind === 'acq'
          ? 'Cuvintele se caută în denumire, descriere, CPV și în produsele achiziției.'
          : 'Prețurile pe luni, pentru un cod CPV: pagina Prețuri.'}
      </p>
      {error ? <ErrorBox error={new Error(error)} /> : <Results kind={kind} params={params} />}
    </Page>
  );
}
